// src/AnalyticsDashboard.js

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';

// Register the components you will use from Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement);

function AnalyticsDashboard() {
    // State to hold our aggregated data and loading status
    const [stats, setStats] = useState({ coaCount: 0, productCount: 0, customerCount: 0 });
    const [coaByProductData, setCoaByProductData] = useState(null);
    const [coaOverTimeData, setCoaOverTimeData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fetch and process data from Firestore
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch all necessary data concurrently
                const [coaSnapshot, productsSnapshot, customersSnapshot] = await Promise.all([
                    getDocs(collection(db, 'coa_records')),
                    getDocs(collection(db, 'products')),
                    getDocs(collection(db, 'customers'))
                ]);

                const coaRecords = coaSnapshot.docs.map(doc => doc.data());

                // 1. --- Process data for Stat Cards ---
                setStats({
                    coaCount: coaSnapshot.size,
                    productCount: productsSnapshot.size,
                    customerCount: customersSnapshot.size,
                });

                // 2. --- Process data for COAs by Product (Pie Chart) ---
                const productCounts = coaRecords.reduce((acc, record) => {
                    acc[record.productName] = (acc[record.productName] || 0) + 1;
                    return acc;
                }, {});

                setCoaByProductData({
                    labels: Object.keys(productCounts),
                    datasets: [{
                        label: '# of COAs',
                        data: Object.values(productCounts),
                        backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
                    }],
                });

                // 3. --- Process data for COAs Over Time (Line Chart) ---
                const monthlyCounts = coaRecords.reduce((acc, record) => {
                    if (record.createdAt) {
                        const date = record.createdAt.toDate();
                        // Group by Year and Month (e.g., "2025-10")
                        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                        acc[monthKey] = (acc[monthKey] || 0) + 1;
                    }
                    return acc;
                }, {});

                const sortedMonths = Object.keys(monthlyCounts).sort();
                setCoaOverTimeData({
                    labels: sortedMonths,
                    datasets: [{
                        label: 'COAs Created',
                        data: sortedMonths.map(month => monthlyCounts[month]),
                        fill: false,
                        borderColor: '#36A2EB',
                        tension: 0.1,
                    }],
                });

            } catch (error) {
                console.error("Error fetching analytics data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Display a loading message while data is being fetched
    if (loading) {
        return <h2>Loading Analytics Dashboard...</h2>;
    }

    return (
        <div className="dashboard-container">
            <h1>Analytics Dashboard</h1>

            {/* --- Stat Cards --- */}
            <div className="stats-grid">
                <div className="stat-card">
                    <h2>{stats.coaCount}</h2>
                    <p>Total COA Records</p>
                </div>
                <div className="stat-card">
                    <h2>{stats.productCount}</h2>
                    <p>Total Products</p>
                </div>
                <div className="stat-card">
                    <h2>{stats.customerCount}</h2>
                    <p>Total Customers</p>
                </div>
            </div>

            {/* --- Charts --- */}
            <div className="charts-grid">
                <div className="chart-container">
                    <h3>COAs by Product</h3>
                    {coaByProductData && <Pie data={coaByProductData} />}
                </div>
                <div className="chart-container">
                    <h3>COA Creation Over Time</h3>
                    {coaOverTimeData && <Line data={coaOverTimeData} />}
                </div>
            </div>
        </div>
    );
}

export default AnalyticsDashboard;